import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { useAtom } from "jotai";
import { cloneDeep } from "lodash";
import { Document, Page, View, Image, PDFViewer, Text } from "@react-pdf/renderer";
import {
  displayMunicNameAtom,
  firmSizeRangeAtom,
  imageResponseAtom,
  selectedFiltersAtom,
  selectedPlaceAtom,
  selectedSpatialLayerAtom,
  selectedSpatialValueAtom,
  smallBusinessMapHighlightAtom,
  smallBusinessMapZoomInAtom,
} from "../../shared/state/atoms";
import axiosNoAuthInstance from "../../components/axiosApiNoAuth";
import { Box, CircularProgress } from "@mui/material";
import styles from "../../pdfs/styles/PdfStyles";
import { DashboardMap } from "../../components/Dashboards/components";
import { useGeoJSON } from "../../shared/hooks";

// --- Shared helpers (duplicated from SmallBusinessMapIntensity for PDF self-containment) ---

function flattenCoords(geometry) {
  const coords = [];
  if (!geometry) return coords;
  if (geometry.type === "Polygon") {
    geometry.coordinates.forEach((ring) => ring.forEach((c) => coords.push(c)));
  } else if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((poly) => poly.forEach((ring) => ring.forEach((c) => coords.push(c))));
  }
  return coords;
}

function pointInRing(lng, lat, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInGeometry(lng, lat, geometry) {
  if (!geometry) return false;
  if (geometry.type === "Polygon") {
    if (!pointInRing(lng, lat, geometry.coordinates[0])) return false;
    for (let h = 1; h < geometry.coordinates.length; h++) {
      if (pointInRing(lng, lat, geometry.coordinates[h])) return false;
    }
    return true;
  }
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.some((poly) => {
      if (!pointInRing(lng, lat, poly[0])) return false;
      for (let h = 1; h < poly.length; h++) {
        if (pointInRing(lng, lat, poly[h])) return false;
      }
      return true;
    });
  }
  return false;
}

function verticalIntersections(lng, geometry) {
  const hits = [];
  const processRing = (ring) => {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [x1, y1] = ring[j];
      const [x2, y2] = ring[i];
      if ((x1 <= lng && x2 > lng) || (x2 <= lng && x1 > lng)) {
        const t = (lng - x1) / (x2 - x1);
        hits.push(y1 + t * (y2 - y1));
      }
    }
  };
  if (geometry.type === "Polygon") {
    geometry.coordinates.forEach(processRing);
  } else if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((poly) => poly.forEach(processRing));
  }
  return hits;
}

function computeCentroid(feature) {
  const coords = flattenCoords(feature?.geometry);
  if (coords.length === 0) return null;
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  let cLng = (minLng + maxLng) / 2;
  let cLat = (minLat + maxLat) / 2;
  if (!pointInGeometry(cLng, cLat, feature.geometry)) {
    const hits = verticalIntersections(cLng, feature.geometry);
    if (hits.length > 0) {
      hits.sort((a, b) => Math.abs(a - cLat) - Math.abs(b - cLat));
      const nearest = hits[0];
      cLat = nearest > cLat ? nearest - 0.01 : nearest + 0.01;
    }
  }
  return { longitude: cLng, latitude: cLat, minLat, maxLat };
}

function computeZoomedViewport(feature) {
  const coords = flattenCoords(feature?.geometry);
  if (coords.length === 0) return null;
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const span = Math.max(maxLng - minLng, maxLat - minLat);
  const zoom = Math.max(4, Math.min(12, Math.log2(360 / span) - 0.5));
  return {
    longitude: (minLng + maxLng) / 2,
    latitude: (minLat + maxLat) / 2,
    zoom, minZoom: zoom - 0.5, maxZoom: zoom + 2, pitch: 0, bearing: 0,
  };
}

function computeClusterViewport(selectedFeature, allFeatures) {
  if (!selectedFeature || !allFeatures?.length) return computeZoomedViewport(selectedFeature);
  const selCoords = flattenCoords(selectedFeature.geometry);
  if (selCoords.length === 0) return null;
  let sMinLng = Infinity, sMaxLng = -Infinity, sMinLat = Infinity, sMaxLat = -Infinity;
  for (const [lng, lat] of selCoords) {
    if (lng < sMinLng) sMinLng = lng; if (lng > sMaxLng) sMaxLng = lng;
    if (lat < sMinLat) sMinLat = lat; if (lat > sMaxLat) sMaxLat = lat;
  }
  const lngPad = (sMaxLng - sMinLng) * 0.1 || 0.1;
  const latPad = (sMaxLat - sMinLat) * 0.1 || 0.1;
  const exMinLng = sMinLng - lngPad, exMaxLng = sMaxLng + lngPad;
  const exMinLat = sMinLat - latPad, exMaxLat = sMaxLat + latPad;
  let cMinLng = sMinLng, cMaxLng = sMaxLng, cMinLat = sMinLat, cMaxLat = sMaxLat;
  for (const f of allFeatures) {
    if (f === selectedFeature) continue;
    const coords = flattenCoords(f.geometry);
    if (coords.length === 0) continue;
    let fMinLng = Infinity, fMaxLng = -Infinity, fMinLat = Infinity, fMaxLat = -Infinity;
    for (const [lng, lat] of coords) {
      if (lng < fMinLng) fMinLng = lng; if (lng > fMaxLng) fMaxLng = lng;
      if (lat < fMinLat) fMinLat = lat; if (lat > fMaxLat) fMaxLat = lat;
    }
    if (fMaxLng >= exMinLng && fMinLng <= exMaxLng && fMaxLat >= exMinLat && fMinLat <= exMaxLat) {
      if (fMinLng < cMinLng) cMinLng = fMinLng; if (fMaxLng > cMaxLng) cMaxLng = fMaxLng;
      if (fMinLat < cMinLat) cMinLat = fMinLat; if (fMaxLat > cMaxLat) cMaxLat = fMaxLat;
    }
  }
  const span = Math.max(cMaxLng - cMinLng, cMaxLat - cMinLat);
  const zoom = Math.max(4, Math.min(12, Math.log2(360 / span) - 0.5));
  return { longitude: (cMinLng + cMaxLng) / 2, latitude: (cMinLat + cMaxLat) / 2, zoom, minZoom: zoom - 0.5, maxZoom: zoom + 2, pitch: 0, bearing: 0 };
}

// --- End shared helpers ---

// Resolve spatial-layer config (same logic as SmallBusinessMapIntensity dashboard).
function resolveLayerConfig(selectedSpatialLayer, selectedPlace) {
  if (!selectedSpatialLayer || !selectedPlace) return null;
  const layerId = selectedSpatialLayer.id;
  const isMetroMuni = layerId === "municipality" && selectedPlace?.category === "A";

  if (isMetroMuni) {
    return {
      mode: "hex",
      taxdata_modelname: "hex7_fte_firmsizefte",
      spatial: "hex7",
      geoFile: "Metros_hex.geo.json",
      vget_field: "hex7",
      vpoly_field: "hex7",
      monoColour: false,
      metroForViewport: selectedPlace?.name,
      extraParams: { metro: selectedPlace?.name, cat_b: selectedPlace?.cat_b },
    };
  }
  if (layerId === "municipality") {
    return {
      mode: "allmunicipalities",
      taxdata_modelname: "metro_fte_firmsizefte",
      spatial: "allmunicipalities",
      geoFile: "LocalMunicipalities_2016.geo.json",
      vget_field: "cat_b",
      vpoly_field: "CAT_B",
      monoColour: false,
      metroForViewport: undefined,
      extraParams: {},
    };
  }
  if (layerId === "district") {
    return {
      mode: "alldistricts",
      taxdata_modelname: "metro_fte_firmsizefte",
      spatial: "alldistricts",
      geoFile: "DistrictMunicipalities_2018.geo.json",
      vget_field: "place_id",
      vpoly_field: "DISTRICT",
      monoColour: true,
      metroForViewport: undefined,
      extraParams: {},
    };
  }
  if (layerId === "province") {
    return {
      mode: "allprovinces",
      taxdata_modelname: "metro_fte_firmsizefte",
      spatial: "allprovinces",
      geoFile: "Province_2018.geo.json",
      vget_field: "place_id",
      vpoly_field: "PROVINCE",
      monoColour: true,
      metroForViewport: undefined,
      extraParams: {},
    };
  }
  return null;
}

function SmallBusinessMapIntensityPdf() {
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  const [selectedSpatialValue] = useAtom(selectedSpatialValueAtom);
  const [imageResponse] = useAtom(imageResponseAtom);
  const [displayMunicname] = useAtom(displayMunicNameAtom);
  const [selectedFilters] = useAtom(selectedFiltersAtom);
  const [firmSizeRange] = useAtom(firmSizeRangeAtom);
  const [zoomIn] = useAtom(smallBusinessMapZoomInAtom);
  const [highlightOn] = useAtom(smallBusinessMapHighlightAtom);

  const [mapData, setMapData] = useState(null);
  const [mapDataGrey, setMapDataGrey] = useState(null);
  const [maxValue, setMaxValue] = useState(0);
  const [minValue, setMinValue] = useState(0);
  const [dataReady, setDataReady] = useState(false);
  const [chartImageUrl, setChartImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const chartRef = useRef();

  const layerConfig = resolveLayerConfig(selectedSpatialLayer, selectedPlace);
  const { geoData } = useGeoJSON(layerConfig?.geoFile ?? "Metros_hex.geo.json");

  const handles = firmSizeRange?.smallBusinessMapIntensity?.handles ?? [50];
  const firmsize_fte_lower = 0;
  const firmsize_fte_upper = handles[0];
  const firmSizeLabel = `0\u2013${handles[0] - 1} FTE`;

  // Fetch map data (same logic as SmallBusinessMapIntensity dashboard component).
  useEffect(() => {
    if (!layerConfig || !geoData || !selectedSpatialValue || !selectedFilters?.singleYear) return;

    const expectedField = layerConfig.vpoly_field;
    const sample = geoData?.features?.[0]?.properties;
    if (!sample || !(expectedField in sample)) return;

    async function fetchData() {
      const vpoly = { type: "FeatureCollection", features: [] };
      const vpolyGrey = { type: "FeatureCollection", features: [] };
      let vmax = 0;
      let vmin = Number.MAX_SAFE_INTEGER;

      try {
        const params = {
          taxdata_modelname: layerConfig.taxdata_modelname,
          spatial: layerConfig.spatial,
          output: "fte",
          year: selectedFilters.singleYear,
          viewas: "Percentage in each area",
          firmsize_fte_lower,
          firmsize_fte_upper,
          ...layerConfig.extraParams,
        };
        const response = await axiosNoAuthInstance.get("/api/map-explorer/", { params });

        if (!response.data || response.data.length === 0) return;

        for (let i = 0; i < response.data.length; i++) {
          for (let j = 0; j < geoData.features.length; j++) {
            if (geoData.features[j].properties[layerConfig.vpoly_field] === response.data[i][layerConfig.vget_field]) {
              const placeName = response.data[i].place_name || "";
              const showPlace = placeName && placeName !== "Not Used" && placeName !== "Not used";
              const tooltipLabel = (displayVal) => {
                const lines = [
                  '<div style="font-size:12px">',
                  '<span style="font-weight:bold">Small Business</span>',
                  `% FTE: ${displayVal}`,
                ];
                if (showPlace) lines.push(`Place: ${placeName}`);
                lines.push('</div>');
                return lines;
              };
              if (typeof response.data[i].display !== "number") {
                const greyFeature = cloneDeep(geoData.features[j]);
                greyFeature.properties.value = "0";
                greyFeature.properties.label = tooltipLabel(response.data[i].display);
                vpolyGrey.features.push(greyFeature);
              } else {
                const feature = cloneDeep(geoData.features[j]);
                feature.properties.value = response.data[i].display;
                if (parseFloat(feature.properties.value) > vmax) vmax = parseFloat(feature.properties.value);
                if (parseFloat(feature.properties.value) < vmin) vmin = parseFloat(feature.properties.value);
                feature.properties.label = tooltipLabel(parseFloat(response.data[i].display.toFixed(2)));
                vpoly.features.push(feature);
              }
              break;
            }
          }
        }

        setMaxValue(vmax);
        setMinValue(vmin === Number.MAX_SAFE_INTEGER ? 0 : vmin);
        setMapData(vpoly);
        setMapDataGrey(vpolyGrey);
        setDataReady(true);
      } catch (err) {
        console.error("SmallBusinessMapIntensityPdf data fetch failed:", err);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpatialLayer, selectedPlace, selectedSpatialValue, selectedFilters?.singleYear, geoData]);

  // Capture map to image once data is loaded and map has rendered.
  useEffect(() => {
    if (!dataReady || !mapData?.features?.length) return;

    const setupMapLoadListener = () => {
      const mapContainer = chartRef.current?.querySelector('.mapboxgl-map');
      if (mapContainer) {
        const mapInstance = mapContainer._map;
        if (mapInstance) {
          if (mapInstance.loaded() && mapInstance.areTilesLoaded()) {
            captureChart();
          } else {
            mapInstance.once('load', () => {
              mapInstance.once('idle', () => {
                captureChart();
              });
            });
          }
        } else {
          setTimeout(captureChart, 15000);
        }
      } else {
        setTimeout(captureChart, 15000);
      }
    };

    const captureChart = () => {
      const options = {
        cacheBust: true,
        quality: 1.0,
        pixelRatio: window.devicePixelRatio,
        onCloneNode: (node) => {
          if (node.tagName === 'LINK' && node.href && node.href.includes('mapbox-gl')) {
            const clonedNode = node.cloneNode(true);
            clonedNode.setAttribute('crossorigin', 'anonymous');
            return clonedNode;
          }
          if (node.tagName === 'STYLE') {
            const styleContent = node.textContent;
            if (styleContent && (styleContent.includes('mapbox') || styleContent.includes('data:image/svg+xml'))) {
              const clonedNode = node.cloneNode(true);
              clonedNode.setAttribute('data-html-to-image-skip', 'true');
              return clonedNode;
            }
          }
          return node;
        },
        filter: (node) => {
          if (node.classList &&
              (node.classList.contains('mapboxgl-ctrl') ||
               node.classList.contains('mapboxgl-control') ||
               node.classList.contains('mapboxgl-ctrl-group'))) {
            return false;
          }
          return true;
        },
        fetchRequestInit: { mode: 'cors', credentials: 'same-origin' },
      };

      toPng(chartRef.current, options)
        .then((dataUrl) => {
          setChartImageUrl(dataUrl);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Failed to capture Map of Intensity:", err);
          const canvas = document.createElement('canvas');
          canvas.width = 800;
          canvas.height = 600;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#f8f9fa';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.font = '16px Arial';
          ctx.fillStyle = '#212529';
          ctx.textAlign = 'center';
          ctx.fillText('Map visualization temporarily unavailable in PDF', canvas.width / 2, canvas.height / 2);
          setChartImageUrl(canvas.toDataURL('image/png'));
          setIsLoading(false);
        });
    };

    const setupTimeoutId = setTimeout(setupMapLoadListener, 3000);
    const fallbackTimeoutId = setTimeout(() => {
      if (!chartImageUrl) captureChart();
    }, 25000);

    return () => {
      clearTimeout(setupTimeoutId);
      clearTimeout(fallbackTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataReady]);

  return (
    <>
      {isLoading ? (
        <div className="absolute left-0 top-0 w-full h-full opacity-30 bg-ccre-gray-900 z-20">
          <Box sx={{ textAlign: "center", position: "relative", top: "40%" }}>
            <CircularProgress color="success" size="3rem" />
          </Box>
        </div>
      ) : chartImageUrl ? (
        <PDFViewer style={{ flex: 1, width: "100%", height: "100vh" }}>
          <Document>
            <Page size="A4" style={styles.page}>
              <Image style={styles.headerLogo} src={`data:image/png;base64,'${imageResponse[13].base64_image}'`}></Image>
              <Text style={styles.headerText}>{selectedSpatialLayer?.name} | {displayMunicname}</Text>
              <View>
                <Text style={{ ...styles.header, backgroundColor: '#1976d2' }}>Small Business</Text>
              </View>
              <View style={styles.section}>
                <Text style={styles.heading}>INTENSITY</Text>
              </View>
              <View style={{ ...styles.section, paddingBottom: 0 }}>
                <Text style={styles.subheading}>Year: {selectedFilters?.singleYear}  |  Firm Size: {firmSizeLabel}</Text>
              </View>
              <View style={{ ...styles.section, height: 600, position: 'absolute', right: 5, top: 90 }}>
                <Image style={styles.image} src={chartImageUrl} />
              </View>
              <View style={{ marginTop: 530 }}>
                <Text style={styles.paragraph}>
                  What share of employment is provided by small businesses in {displayMunicname}?
                </Text>
                <Text style={styles.paragraph}>
                  This map shows the share of total FTE employment provided by small businesses (firms with fewer than {handles[0]} FTE employees) in each area. A higher percentage indicates that the local economy relies more heavily on small businesses for jobs. Use the spatial layer selector to compare municipalities, districts and provinces.
                </Text>
              </View>
              <View style={styles.footerContainer}>
                <Text style={styles.footer}>
                  www.spatialtaxdata.org.za
                </Text>
              </View>
            </Page>
          </Document>
        </PDFViewer>
      ) : null}
      {dataReady && mapData?.features?.length > 0 && (() => {
        const isCountryView = layerConfig?.mode !== "hex";
        const geoFeature = isCountryView
          ? geoData?.features?.find((f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue)
          : null;

        // Compute viewport (respects zoom toggle + spatial-layer-specific zoom behaviour)
        const baseVp = { longitude: 25, latitude: -29, zoom: 4.55, minZoom: 4.45, maxZoom: 4.65, pitch: 0, bearing: 0 };
        let pdfViewport;
        if (!isCountryView) {
          pdfViewport = undefined;
        } else if (zoomIn && geoFeature && layerConfig?.mode !== "allprovinces") {
          if (layerConfig?.mode === "allmunicipalities" || layerConfig?.mode === "alldistricts") {
            // Zoom to show selected place + surrounding neighbours
            pdfViewport = computeClusterViewport(geoFeature, geoData?.features) || baseVp;
          } else {
            pdfViewport = computeZoomedViewport(geoFeature) || baseVp;
          }
        } else {
          // Country view with viewport shift for edge places
          pdfViewport = baseVp;
          if (geoFeature) {
            const placeCentroid = computeCentroid(geoFeature);
            if (placeCentroid) {
              const topEdge = baseVp.latitude + 9;
              const bottomEdge = baseVp.latitude - 7;
              const topHeadroom = 5.3;
              const bottomHeadroom = 1.5;
              let shift = 0;
              if (placeCentroid.latitude > topEdge - topHeadroom) {
                shift = placeCentroid.latitude - (topEdge - topHeadroom);
              }
              if (placeCentroid.minLat < bottomEdge + bottomHeadroom) {
                const bottomShift = placeCentroid.minLat - (bottomEdge + bottomHeadroom);
                if (shift === 0 || Math.abs(bottomShift) > Math.abs(shift)) shift = bottomShift;
              }
              if (shift !== 0) pdfViewport = { ...baseVp, latitude: baseVp.latitude + shift };
            }
          }
        }

        // Compute highlight feature
        const pdfHighlight = highlightOn && isCountryView ? geoFeature || null : null;

        // Compute pinned tooltip (bound to highlight toggle)
        let pdfPinnedTooltip = null;
        if (isCountryView && highlightOn && geoFeature) {
          const centroid = computeCentroid(geoFeature);
          if (centroid) {
            const dataFeature =
              mapData?.features?.find((f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue) ||
              mapDataGrey?.features?.find((f) => f.properties[layerConfig.vpoly_field] === selectedSpatialValue);
            if (dataFeature?.properties?.label) {
              const text = dataFeature.properties.label
                .filter((l) => !l.startsWith("<div") && !l.startsWith("</div"))
                .map((l) => l.replace(/<[^>]*>/g, ""))
                .join("\n");
              pdfPinnedTooltip = { ...centroid, text };
            }
          }
        }

        return (
          <div style={{ position: "absolute", left: "-10000px" }}>
            <div ref={chartRef}>
              <div style={{ height: 1000, aspectRatio: 1 }}>
                <DashboardMap
                  key={1}
                  reportKey={1}
                  data={mapData}
                  data_grey={mapDataGrey}
                  metro={layerConfig?.metroForViewport}
                  output="fte"
                  viewAs="Absolute"
                  maxValue={parseFloat(maxValue)}
                  minValue={parseFloat(minValue)}
                  monoColour={layerConfig?.monoColour || false}
                  customViewport={pdfViewport}
                  highlightFeature={pdfHighlight}
                  pinnedTooltip={pdfPinnedTooltip}
                />
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}

export default SmallBusinessMapIntensityPdf;
